import {useQuery} from '@apollo/client'
import React, {useEffect} from 'react'
import {Accordion, Spinner} from 'react-bootstrap'
import {FormattedMessage, useIntl, IntlShape} from 'react-intl'
import moment from 'moment'
import {toast} from 'react-toastify'
import {GET_COMMISSION_REPORT_LOGS} from '../../../gql/queries/commissionReportsApiQueries'

interface ICommissionReportActivityTabProps {
  id: string
}

interface ICommissionReportLogEntry {
  id: string
  oldValue: unknown
  newValue: unknown
  updatedAt: string
  user?: {
    id: string
    firstName?: string | null
    lastName?: string | null
  } | null
  changes?: string[] | null
}

interface ICommissionReportLogsResponse {
  commissionReportLogsByReportId: ICommissionReportLogEntry[]
}

type DifferenceRow = {
  field: string
  oldValue: unknown
  newValue: unknown
}

const toDict = (value: unknown): any => {
  if (value == null) return {}
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }
  return value
}

const isPlainMessage = (oldVal: unknown, newVal: unknown) => {
  return (typeof oldVal === 'string' && oldVal.trim() !== '') || (typeof newVal === 'string' && newVal.trim() !== '')
}

const areValuesEqual = (valueA: unknown, valueB: unknown) => {
  if (valueA === valueB) return true
  // eslint-disable-next-line eqeqeq
  if (valueA == null || valueB == null) return valueA == valueB
  if (typeof valueA === 'object' && typeof valueB === 'object') {
    try {
      return JSON.stringify(valueA) === JSON.stringify(valueB)
    } catch {
      return false
    }
  }
  return false
}

const renderValue = (value: unknown, intl: IntlShape) => {
  if (value === null || value === undefined || value === '') return intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
  if (typeof value === 'boolean') return value ? intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'}) : intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'})
  if (typeof value === 'object') {
    return <pre className='mb-0'>{JSON.stringify(value, null, 2)}</pre>
  }
  return String(value)
}

const buildDifferences = (oldValue: unknown, newValue: unknown): DifferenceRow[] => {
  const oldParsed = toDict(oldValue)
  const newParsed = toDict(newValue)

  if (isPlainMessage(oldParsed, newParsed)) {
    return []
  }

  const oldObj =
    typeof oldParsed === 'object' && oldParsed !== null && !Array.isArray(oldParsed)
      ? (oldParsed as Record<string, unknown>)
      : {}
  const newObj =
    typeof newParsed === 'object' && newParsed !== null && !Array.isArray(newParsed)
      ? (newParsed as Record<string, unknown>)
      : {}

  const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)])

  return Array.from(allKeys).reduce((diff, key) => {
    if (areValuesEqual(oldObj[key], newObj[key])) {
      return diff
    }

    diff.push({
      field: key,
      oldValue: oldObj[key],
      newValue: newObj[key],
    })

    return diff
  }, [] as DifferenceRow[])
}

export const CommissionReportActivityTab: React.FC<ICommissionReportActivityTabProps> = ({id}) => {
  const intl = useIntl()
  const colon = ':'
  const dash = '-'
  const emDash = '—'
  const space = ' '
  const system = intl.formatMessage({id: 'commissions.reports.activity.system', defaultMessage: 'System'})
  const {data, loading, error} = useQuery<ICommissionReportLogsResponse>(GET_COMMISSION_REPORT_LOGS, {
    variables: {reportId: Number(id)},
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (error) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  if (loading || !data) {
    return (
      <div className='cardContainer'>
        <div className='kt-heading pb-2 custom-border-brand d-flex align-items-center justify-content-between'>
          <span><FormattedMessage id='commissions.reports.activity.title' defaultMessage='Activity' /></span>
        </div>
        <div className='d-flex flex-center' style={{height: '40vh'}}>
          <Spinner animation='border' />
        </div>
      </div>
    )
  }

  if (data.commissionReportLogsByReportId.length === 0) {
    return (
      <div className='cardContainer'>
        <div className='kt-heading pb-2 custom-border-brand d-flex align-items-center justify-content-between'>
          <span><FormattedMessage id='commissions.reports.activity.title' defaultMessage='Activity' /></span>
        </div>
        <div className='d-flex flex-center' style={{height: '40vh'}}>
          <h4><FormattedMessage id='commissions.reports.activity.empty' defaultMessage='There is no activity recorded at the moment' /></h4>
        </div>
      </div>
    )
  }

  const first = data.commissionReportLogsByReportId[0]
  const restArr = data.commissionReportLogsByReportId.slice(1)

  return (
    <div className='policy-activity'>
      <div className='kt-heading kt-heading--md custom-border-brand'>
        <span><FormattedMessage id='commissions.reports.activity.logsTitle' defaultMessage='Commission report logs' /></span>
      </div>
      <Accordion defaultActiveKey={`${data.commissionReportLogsByReportId.length}`}>
        {restArr.reverse().map((log, index) => {
          const differences = buildDifferences(log.oldValue, log.newValue)
          const oldParsed = toDict(log.oldValue)
          const newParsed = toDict(log.newValue)
          const sequence = data.commissionReportLogsByReportId.length - index
          const userName = [log.user?.firstName, log.user?.lastName]
            .filter(Boolean)
            .join(' ')
            .trim() || system

          return (
            <Accordion.Item eventKey={`${sequence}`} key={log.id} className='log-entry'>
              <Accordion.Header className='fw-bold'>
                <span>{sequence}{colon}</span>&nbsp;{space}
                <span>{moment(log.updatedAt).format('YYYY-MM-DD HH:mm:ss')}</span>&nbsp;{dash}&nbsp;
                <span className='fw-bold'>{userName}</span>
              </Accordion.Header>
              <Accordion.Body>
                <p>
                  <strong><FormattedMessage id='commissions.reports.activity.changes' defaultMessage='Changes:' /></strong>{space}
                  {Array.isArray(log.changes) && log.changes.length > 0
                    ? log.changes.join(', ')
                    : emDash}
                </p>
                {isPlainMessage(oldParsed, newParsed) ? (
                  <div className='text-muted'><FormattedMessage id='commissions.reports.activity.noStructured' defaultMessage='No structured changes to display.' /></div>
                ) : (
                  <table className='table table-striped'>
                    <thead>
                      <tr>
                        <th className='fw-bold'><FormattedMessage id='commissions.reports.activity.field' defaultMessage='Field' /></th>
                        <th className='fw-bold'><FormattedMessage id='commissions.reports.activity.prevValue' defaultMessage='Previous Value' /></th>
                        <th className='fw-bold'><FormattedMessage id='commissions.reports.activity.newValue' defaultMessage='New Value' /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {differences.length > 0 ? (
                        differences.map((difference) => (
                          <tr key={difference.field}>
                            <td>{difference.field}</td>
                            <td>{renderValue(difference.oldValue, intl)}</td>
                            <td>{renderValue(difference.newValue, intl)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3}><FormattedMessage id='commissions.reports.activity.noDifferences' defaultMessage='There are no differences' /></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </Accordion.Body>
            </Accordion.Item>
          )
        })}
        <Accordion.Item eventKey='1' className='log-entry'>
          <Accordion.Header className='font-bold'>
            <span>{1}{colon}</span>&nbsp; {moment(first.updatedAt).format('YYYY-MM-DD HH:mm:ss')} {dash} &nbsp;
            <span className='fw-bold'>
              {[first.user?.firstName, first.user?.lastName].filter(Boolean).join(' ').trim() || system}
            </span>{space}
          </Accordion.Header>
          <Accordion.Body>
            <p>
              <strong><FormattedMessage id='commissions.reports.activity.changes' defaultMessage='Changes:' /></strong>{space}
              {Array.isArray(first.changes) && first.changes.length > 0
                ? first.changes.join(', ')
                : emDash}
            </p>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  )
}
